import type { Grant, GrantOverride, Override, Permission } from "../../../api/access/accessModel";
import { NEEDS_REASON, permissionRank } from "./permissionCatalogue";

/**
 * A group's set as the screens change it: switch a permission on or off, choose the kinds it
 * curates. Curate is granted per item kind (accounts.md GS-2); switching it on gives every
 * kind, as the redesign's Curate toggle does (R3-2). Pure; the server checks every write.
 */

/** The server's own key of a grant: `view`, or `curate:<kind>`. */
export function grantKey(grant: Grant): string {
  return grant.itemKind === null ? grant.permission : `${grant.permission}:${grant.itemKind}`;
}

export function holds(grants: readonly Grant[], permission: Permission): boolean {
  return grants.some((grant) => grant.permission === permission);
}

/** The kinds a set curates, in the order of `itemKinds`. */
export function curatedKinds(grants: readonly Grant[], itemKinds: readonly string[]): readonly string[] {
  const curated = new Set(grants.flatMap((grant) => (grant.permission === "curate" && grant.itemKind !== null ? [grant.itemKind] : [])));
  return itemKinds.filter((kind) => curated.has(kind));
}

/** The set with `permission` switched on or off; Curate on means every kind. */
export function withPermission(grants: readonly Grant[], permission: Permission, on: boolean, itemKinds: readonly string[]): readonly Grant[] {
  const others = grants.filter((grant) => grant.permission !== permission);
  if (!on) return others;
  const added: readonly Grant[] = permission === "curate"
    ? itemKinds.map((kind) => ({ permission, itemKind: kind }))
    : [{ permission, itemKind: null }];
  return sortedGrants([...others, ...added]);
}

/** The set curating exactly `kinds`; none takes Curate away. */
export function withCuratedKinds(grants: readonly Grant[], kinds: readonly string[]): readonly Grant[] {
  return sortedGrants([...grants.filter((grant) => grant.permission !== "curate"), ...kinds.map((kind) => ({ permission: "curate" as const, itemKind: kind }))]);
}

/** Whether a change from `before` to `after` switches on the server's keys, which needs a reason. */
export function switchesOnServerKeys(before: readonly Grant[], after: readonly Grant[]): boolean {
  return holds(after, NEEDS_REASON) && !holds(before, NEEDS_REASON);
}

export function sameGrants(left: readonly Grant[], right: readonly Grant[]): boolean {
  const keys = new Set(left.map(grantKey));
  return left.length === right.length && right.every((grant) => keys.has(grantKey(grant)));
}

/** The permissions a set names, each once, in screen order. */
export function permissionsOf(grants: readonly Grant[]): readonly Permission[] {
  return [...new Set(grants.map((grant) => grant.permission))].sort((left, right) => permissionRank(left) - permissionRank(right));
}

function sortedGrants(grants: readonly Grant[]): readonly Grant[] {
  return [...grants].sort((left, right) => permissionRank(left.permission) - permissionRank(right.permission) || grantKey(left).localeCompare(grantKey(right)));
}

/**
 * An included installation group's three-way choice per permission (PD-3 (b)): as the
 * installation sets it, on here, or off here. Curate's kinds go together.
 */
export type InclusionChoice = "inherit" | Override;

export function inclusionChoice(overrides: readonly GrantOverride[], permission: Permission): InclusionChoice {
  return overrides.find((override) => override.permission === permission)?.override ?? "inherit";
}

/**
 * The overrides with `permission` set to `choice`. On or off for Curate names every kind; off
 * names only the kinds the installation gives, which are the only ones it could switch off.
 */
export function withInclusionChoice(
  overrides: readonly GrantOverride[],
  permission: Permission,
  choice: InclusionChoice,
  installationGrants: readonly Grant[],
  itemKinds: readonly string[],
): readonly GrantOverride[] {
  const others = overrides.filter((override) => override.permission !== permission);
  if (choice === "inherit") return others;
  const granted = choice === "on"
    ? withPermission([], permission, true, itemKinds)
    : installationGrants.filter((grant) => grant.permission === permission);
  return [...others, ...granted.map((grant) => ({ ...grant, override: choice }))];
}

/** The overrides with Curate switched on here for exactly `kinds`; the other choices stay. */
export function withCuratedKindsHere(overrides: readonly GrantOverride[], kinds: readonly string[]): readonly GrantOverride[] {
  return [...overrides.filter((override) => override.permission !== "curate"), ...kinds.map((kind) => ({ permission: "curate" as const, itemKind: kind, override: "on" as const }))];
}

export function sameOverrides(left: readonly GrantOverride[], right: readonly GrantOverride[]): boolean {
  const key = (override: GrantOverride) => `${grantKey(override)}=${override.override}`;
  const keys = new Set(left.map(key));
  return left.length === right.length && right.every((override) => keys.has(key(override)));
}

/**
 * What an included installation group gives in one project: its own set, less what is
 * switched off here, plus what is switched on here (PD-3 (b), the server's `granted_here`).
 * Whether a change switches on the server's keys is judged on this, not on one choice alone.
 */
export function grantedHere(base: readonly Grant[], overrides: readonly GrantOverride[]): readonly Grant[] {
  const off = new Set(overrides.filter((override) => override.override === "off").map(grantKey));
  const kept = base.filter((grant) => !off.has(grantKey(grant)));
  const keptKeys = new Set(kept.map(grantKey));
  const added = overrides.filter((override) => override.override === "on" && !keptKeys.has(grantKey(override))).map(({ permission, itemKind }) => ({ permission, itemKind }));
  return sortedGrants([...kept, ...added]);
}
